import React, { useState, useRef, useEffect, useMemo } from "react"
import InputField from "components/input-field"
import { areaIcons, areas } from "pages/FlowBuilderPage/constants"
import { Flow } from "types/flowBuilder"
import { removeAccentsAndSpecialChars } from "utils/removeSpecialChars"
import { TbCrown } from "react-icons/tb"
import Flex from "components/flex"
import useFreePlan from "hooks/useFreePlan"
import { useAtom } from "jotai"
import { plansModalAtom } from "pages/BasePage/atoms"
interface SelectProps {
	options: Record<string, Flow[]> | undefined
	onSelect: (flowName: string) => void
	whitoutCreate?: boolean
	placeholder: string
	clearable?: boolean
	disabled?: boolean
}

export default function Select({
	options,
	onSelect,
	placeholder,
	clearable,
	disabled,
}: SelectProps) {
	const [openOptions, setOpenOptions] = useState(false)
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
	const [value, setValue] = useState("")
	const optionsRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const optionRefs = useRef<(HTMLDivElement | null)[]>([])
	const premiumHoverGroupClass =
		"group-hover:w-fit group-hover:gap-1 group-hover:p-1 transition-all"
	const highlightedPremiumHoverClass = "w-fit gap-1 p-1 transition-all  "
	const [, setIsOpen] = useAtom(plansModalAtom)

	const isFreeUser = useFreePlan()

	const filteredGroupedOptions = useMemo(() => {
		if (!options) return {}
		const grouped: Record<string, Flow[]> = {}
		areas.forEach((area) => {
			const list = options[area.value]
			if (!list) return
			const filtered = list
				.filter((f) =>
					removeAccentsAndSpecialChars(f.name.toLowerCase()).includes(
						removeAccentsAndSpecialChars(value.toLowerCase()),
					),
				)
				.sort((a, b) => a.name.localeCompare(b.name))
			if (filtered.length) grouped[area.value] = filtered
		})
		return grouped
	}, [options, value])

	const flattenedOptions = useMemo(
		() =>
			areas.reduce<Flow[]>((acc, area) => {
				const list = filteredGroupedOptions[area.value]
				return list ? acc.concat(list) : acc
			}, []),
		[filteredGroupedOptions],
	)

	useEffect(() => {
		const onClickOutside = (e: MouseEvent) => {
			if (
				optionsRef.current &&
				!optionsRef.current.contains(e.target as Node)
			) {
				setOpenOptions(false)
			}
		}
		if (openOptions) document.addEventListener("mousedown", onClickOutside)
		return () => document.removeEventListener("mousedown", onClickOutside)
	}, [openOptions])

	useEffect(() => {
		if (
			selectedIndex !== null &&
			optionRefs.current[selectedIndex]?.scrollIntoView
		) {
			optionRefs.current[selectedIndex]!.scrollIntoView({
				block: "nearest",
			})
		}
	}, [selectedIndex])

	useEffect(() => {
		setSelectedIndex(null)
	}, [value])

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !openOptions) {
			setOpenOptions(true)
			setSelectedIndex(0)
			e.preventDefault()
			return
		}
		if (!openOptions) return

		const max = flattenedOptions.length
		let nextIndex = selectedIndex ?? -1

		if (e.key === "ArrowDown") {
			e.preventDefault()
			nextIndex = (nextIndex + 1) % max
			setSelectedIndex(nextIndex)
		}
		if (e.key === "ArrowUp") {
			e.preventDefault()
			nextIndex = (nextIndex - 1 + max) % max
			setSelectedIndex(nextIndex)
		}
		if (e.key === "Enter" && selectedIndex !== null) {
			const sel = flattenedOptions[selectedIndex]

			if (sel.is_premium && isFreeUser) {
				setIsOpen(true)
				return
			}
			setValue(sel.name)
			onSelect(sel.id)
			setOpenOptions(false)
			setSelectedIndex(null)
		}
	}

	return (
		<div
			className="relative"
			ref={optionsRef}
			onClick={() => {
				if (disabled) return
				setOpenOptions((o) => !o)
				if (clearable && !openOptions) {
					setValue("")
					onSelect("")
				}
			}}
		>
			<div
				className="relative"
				onClick={(e) => {
					e.stopPropagation()
					if (!disabled) setOpenOptions(true)
					inputRef.current?.focus()
				}}
			>
				<InputField
					className={`h-[56px] w-[400px] !rounded-[16px] !p-4 bg-[white] shadow-input-shadow !border-[#F0F0F0] font-semibold placeholder:font-semibold focus:placeholder:text-transparent focus:outline-[1px] focus:outline-[#3083FF]`}
					onChange={(e) => {
						setValue(e.target.value)
					}}
					onKeyDown={handleKeyDown}
					value={value}
					ref={inputRef}
					disabled={disabled}
					maxLength={30}
					placeholder={placeholder}
				/>
			</div>

			{openOptions && (
				<div className="absolute top-[64px] left-0 h-fit w-full bg-white shadow-outlined-hover rounded-[16px] z-50 pr-[0.5px] overflow-hidden">
					<div className="overflow-y-auto max-h-[190px] 0xl:max-h-[300px] bg-brand-white-2">
						{flattenedOptions.length === 0 ? (
							<div className="p-2 text-[#B0B0B0] text-[14px] font-semibold">
								Documento não encontrado
							</div>
						) : (
							(() => {
								let offset = 0
								return Object.entries(
									filteredGroupedOptions,
								).map(([group, flows]) => {
									const start = offset
									const items = flows.map((flow, i) => {
										const idx = start + i
										const isHighlighted =
											idx === selectedIndex
										const isSelectedValue =
											flow.name === value

										return (
											<div
												key={flow.id}
												id={`option-${idx}`}
												ref={(el) =>
													(optionRefs.current[idx] =
														el)
												}
												className={`group p-2 cursor-pointer text-cta-2 h-[32px] flex items-center justify-between text-brand-gray-3 bg-brand-white-2 hover:bg-[#eeecec] ${
													isHighlighted
														? "!bg-[#eeecec]"
														: ""
												} ${
													isSelectedValue
														? "font-semibold bg-[#F5F5F5] !text-dark-blue-1"
														: ""
												}`}
												onClick={() => {
													if (
														isFreeUser &&
														flow.is_premium
													) {
														setIsOpen(true)
													} else {
														setValue(flow.name)
														onSelect(flow.id)
														setOpenOptions(false)
													}
												}}
											>
												<div className="flex items-center justify-between gap-2">
													<div className="w-[20px] h-[20px] flex items-center justify-center">
														{
															areaIcons[
																group as keyof typeof areaIcons
															]
														}
													</div>
													<p
														className={`flex-1 truncate whitespace-nowrap max-w-[320px] ${
															isFreeUser &&
															"group-hover:max-w-[270px]"
														} overflow-hidden ${
															isHighlighted &&
															"!max-w-[270px]"
														}`}
													>
														{flow.name}
													</p>
												</div>
												{flow.is_premium &&
													isFreeUser && (
														<Flex
															className={`w-[24px] h-[24px] justify-center rounded-full bg-[#FDECC8] ${premiumHoverGroupClass} ${
																isHighlighted &&
																highlightedPremiumHoverClass
															}`}
														>
															<TbCrown
																size={16}
																color="#F7A428"
																fill="#F7A428"
															/>
															<p
																className={`text-small-1 text-[#F7A428] font-bold hidden group-hover:block ${
																	isHighlighted &&
																	"!block"
																}`}
															>
																Premium
															</p>
														</Flex>
													)}
											</div>
										)
									})
									offset += flows.length
									return (
										<div key={group}>
											{!value && (
												<p className="text-cta-2 text-[#B0B0B0] pl-2 h-[32px] flex items-center">
													{
														areas.find(
															(a) =>
																a.value ===
																group,
														)?.label
													}
												</p>
											)}
											{items}
										</div>
									)
								})
							})()
						)}
					</div>
				</div>
			)}
		</div>
	)
}
